/**
 * "Accepted when": how a destination reads its buyer's reply.
 *
 * Without rules a 2xx reply is a sale, which is how every destination worked
 * before. Rules say what the reply must hold (`success` equals `true`), and a
 * 2xx that fails them is recorded as a rejection with the buyer's reason.
 *
 * The "try it" box asks the server (`POST /api/v1/destinations/acceptance/check`)
 * rather than judging in the browser, so this screen and the delivery engine
 * run the same evaluator and cannot disagree.
 */
import { useEffect, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Divider, Grid, Group, List, NumberInput,
  Select, Stack, Switch, Text, Textarea, TextInput, ThemeIcon,
} from '@mantine/core';
import { IconCheck, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import type { components } from '../api/schema';
import { useCheckAcceptance, useEnums } from '../api/queries';
import { formatGBP } from '../theme';
import { useVocab } from '../vocab';

export type Acceptance = components['schemas']['Acceptance'];
type Rule = components['schemas']['AcceptRule'];
type Verdict = components['schemas']['Verdict'];
type Legacy = components['schemas']['LegacyResponseSettings'];

export const emptyAcceptance = (): Acceptance => ({ rules: [], ping_rules: [], use_response_price: false });

/** What is saved: blank rows dropped, blank paths left out. */
export function cleanAcceptance(a: Acceptance): Acceptance {
  const keep = (rs: Rule[] | undefined) =>
    (rs ?? []).filter((r) => r.operator === 'body_contains' || (r.path ?? '').trim());
  const path = (p: string | null | undefined) => (p && p.trim() ? p.trim() : undefined);
  return {
    rules: keep(a.rules),
    ping_rules: keep(a.ping_rules),
    reject_reason_path: path(a.reject_reason_path),
    buyer_lead_id_path: path(a.buyer_lead_id_path),
    price_path: path(a.price_path),
    use_response_price: !!a.use_response_price,
  };
}

const valueText = (v: unknown): string =>
  v == null ? '' : Array.isArray(v) ? v.join(', ') : typeof v === 'string' ? v : JSON.stringify(v);

function RuleRows({
  rules,
  onChange,
  addLabel,
}: {
  rules: Rule[];
  onChange: (next: Rule[]) => void;
  addLabel: string;
}) {
  const { data: enums } = useEnums();
  const operators = (enums?.acceptance_operators ?? []).map((o) => ({ value: o.value, label: o.label }));
  const set = (i: number, patch: Partial<Rule>) =>
    onChange(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <Stack gap={6}>
      {rules.map((r, i) => (
        <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
          {r.operator !== 'body_contains' && (
            <TextInput
              size="xs"
              style={{ flex: 1 }}
              aria-label="Where in the reply"
              placeholder="success, data.status, errors.0"
              value={r.path ?? ''}
              onChange={(e) => set(i, { path: e.currentTarget.value })}
            />
          )}
          <Select
            size="xs"
            w={r.operator === 'body_contains' ? undefined : 170}
            style={r.operator === 'body_contains' ? { flex: 1 } : undefined}
            aria-label="Check"
            data={operators}
            value={r.operator}
            allowDeselect={false}
            onChange={(op) => op && set(i, { operator: op })}
          />
          {r.operator !== 'exists' && (
            <TextInput
              size="xs"
              style={{ flex: 1 }}
              aria-label="Value"
              placeholder={r.operator === 'in' ? 'accepted, sold' : 'true'}
              value={valueText(r.value)}
              onChange={(e) => set(i, { value: e.currentTarget.value })}
            />
          )}
          <ActionIcon
            variant="subtle"
            color="red"
            aria-label="Remove rule"
            onClick={() => onChange(rules.filter((_, j) => j !== i))}
          >
            <IconTrash size={15} />
          </ActionIcon>
        </Group>
      ))}
      <div>
        <Button
          size="compact-xs"
          variant="light"
          leftSection={<IconPlus size={13} />}
          onClick={() => onChange([...rules, { path: '', operator: 'equals', value: '' }])}
        >
          {addLabel}
        </Button>
      </div>
    </Stack>
  );
}

function VerdictView({ verdict, invalid }: { verdict: Verdict; invalid?: string | null }) {
  const v = useVocab();
  return (
    <Stack gap={6}>
      <Group gap="xs">
        <Badge color={verdict.accepted ? 'chartGreen' : 'red'} variant="light" size="lg">
          {verdict.accepted ? 'Accepted' : 'Rejected'}
        </Badge>
        <Text size="sm" c="dimmed">
          {verdict.accepted
            ? `Counted as a sale${verdict.checked ? ': every rule passed' : `: a ${verdict.status} reply with no rules`}.`
            : verdict.reason
              ? verdict.reason.replace(/^Buyer /, `The ${v.one('buyer')} `) + '.'
              : `A ${verdict.status} reply is never a sale.`}
        </Text>
      </Group>
      {verdict.checks.length > 0 && (
        <List spacing={2} size="sm" center>
          {verdict.checks.map((c, i) => (
            <List.Item
              key={i}
              icon={
                <ThemeIcon size={18} radius="xl" color={c.passed ? 'chartGreen' : 'red'} variant="light">
                  {c.passed ? <IconCheck size={12} /> : <IconX size={12} />}
                </ThemeIcon>
              }
            >
              {c.rule}
              {c.found != null && <Text span size="xs" c="dimmed">{`  (found: ${c.found})`}</Text>}
            </List.Item>
          ))}
        </List>
      )}
      {(verdict.buyer_reason || verdict.buyer_lead_id || verdict.response_price != null) && (
        <Group gap="lg">
          {verdict.buyer_reason && <Text size="xs" c="dimmed">Their reason <b>{verdict.buyer_reason}</b></Text>}
          {verdict.buyer_lead_id && <Text size="xs" c="dimmed">Their {v.one('lead')} id <b>{verdict.buyer_lead_id}</b></Text>}
          {verdict.response_price != null && (
            <Text size="xs" c="dimmed">
              Their price <b>{formatGBP(verdict.response_price)}</b>
              {verdict.price_from_response ? ' (sold at this price)' : ' (recorded only)'}
            </Text>
          )}
        </Group>
      )}
      {invalid && <Text size="xs" c="red">{`Cannot be saved yet: ${invalid}.`}</Text>}
    </Stack>
  );
}

/** The "try it" box: paste a reply, see how the rules read it. */
function TryIt({ acceptance, isPingPost }: { acceptance: Acceptance; isPingPost: boolean }) {
  const v = useVocab();
  const check = useCheckAcceptance();
  const [sample, setSample] = useState('{"success": false, "message": "Already held"}');
  const [status, setStatus] = useState<number>(200);
  const [ping, setPing] = useState(false);
  const key = JSON.stringify([acceptance, sample, status, ping]);

  // Judged as you type, after a short pause.
  useEffect(() => {
    const t = setTimeout(() => {
      check.mutate({ acceptance, response_body: sample, status, ping });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>Try it</Text>
      <Text size="xs" c="dimmed">{`Paste a reply the ${v.one('buyer')} might send. Nothing is sent anywhere.`}</Text>
      <Grid gutter="xs">
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <Textarea
            aria-label="Sample reply"
            autosize
            minRows={3}
            maxRows={10}
            ff="monospace"
            value={sample}
            onChange={(e) => setSample(e.currentTarget.value)}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Stack gap="xs">
            <NumberInput
              label="HTTP status"
              size="xs"
              min={100}
              max={599}
              value={status}
              onChange={(n) => setStatus(Number(n) || 200)}
            />
            {isPingPost && (
              <Switch
                size="xs"
                label="This is the ping reply"
                checked={ping}
                onChange={(e) => setPing(e.currentTarget.checked)}
              />
            )}
          </Stack>
        </Grid.Col>
      </Grid>
      {check.data && <VerdictView verdict={check.data.verdict} invalid={check.data.invalid} />}
      {check.error && <Text size="xs" c="red">{check.error.message}</Text>}
    </Stack>
  );
}

export function AcceptanceEditor({
  value,
  onChange,
  isPingPost,
  legacy,
}: {
  value: Acceptance;
  onChange: (next: Acceptance) => void;
  isPingPost: boolean;
  legacy?: Legacy | null;
}) {
  const v = useVocab();
  const rules = value.rules ?? [];
  const pingRules = value.ping_rules ?? [];
  const set = (patch: Partial<Acceptance>) => onChange({ ...value, ...patch });

  function convertLegacy() {
    if (!legacy?.success_path) return;
    set({
      rules: [...rules, { path: legacy.success_path, operator: 'equals', value: 'true' }],
      reject_reason_path: value.reject_reason_path || legacy.error_path || undefined,
    });
  }

  return (
    <Card withBorder padding="md">
      <Stack>
        <div>
          <Text size="sm" fw={600}>Accepted when</Text>
          <Text size="xs" c="dimmed">
            {rules.length === 0
              ? `No rules: any 2xx reply counts as a sale. Add rules to read what the ${v.one('buyer')} actually said.`
              : `A 2xx reply counts as a sale only when every rule passes. Anything else is a rejection, and the ${v.one('lead')} moves on to the next ${v.one('destination')}.`}
          </Text>
        </div>

        {legacy?.success_path && rules.length === 0 && (
          <Alert color="chartAmber" variant="light" title="Saved, but never checked">
            <Stack gap="xs">
              <Text size="sm">
                {`This ${v.one('destination')} has a success path of "${legacy.success_path}"${
                  legacy.error_path ? ` and an error path of "${legacy.error_path}"` : ''
                } from an older editor. They have never been checked: every 2xx reply counts as a sale.`}
              </Text>
              <div>
                <Button size="compact-xs" variant="light" onClick={convertLegacy}>
                  {`Check that ${legacy.success_path} equals true`}
                </Button>
              </div>
            </Stack>
          </Alert>
        )}

        <RuleRows
          rules={rules}
          onChange={(next) => set({ rules: next })}
          addLabel={isPingPost ? 'Add a rule for the post reply' : 'Add a rule'}
        />

        {isPingPost && (
          <>
            <Divider label="Ping" labelPosition="left" />
            <Text size="xs" c="dimmed">
              {`With no ping rules, a 2xx ping is enough to send the ${v.one('lead')}.`}
            </Text>
            <RuleRows
              rules={pingRules}
              onChange={(next) => set({ ping_rules: next })}
              addLabel="Add a rule for the ping reply"
            />
          </>
        )}

        <Divider label="Read from the reply" labelPosition="left" />
        <Grid gutter="sm">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TextInput
              size="xs"
              label="Their reason for a rejection"
              placeholder="message"
              value={value.reject_reason_path ?? ''}
              onChange={(e) => set({ reject_reason_path: e.currentTarget.value })}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TextInput
              size="xs"
              label={`Their id for the ${v.one('lead')}`}
              placeholder="data.lead_id"
              value={value.buyer_lead_id_path ?? ''}
              onChange={(e) => set({ buyer_lead_id_path: e.currentTarget.value })}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TextInput
              size="xs"
              label="Their price"
              placeholder="price"
              value={value.price_path ?? ''}
              onChange={(e) => set({ price_path: e.currentTarget.value })}
            />
          </Grid.Col>
        </Grid>
        <Switch
          size="sm"
          label={`Sell at the price the ${v.one('buyer')} states`}
          description="Off: their price is recorded on the attempt, and the configured price is charged."
          checked={!!value.use_response_price}
          disabled={!value.price_path?.trim()}
          onChange={(e) => set({ use_response_price: e.currentTarget.checked })}
        />

        <Divider />
        <TryIt acceptance={cleanAcceptance(value)} isPingPost={isPingPost} />
      </Stack>
    </Card>
  );
}
